import React from 'react';
import { GameState, PlayerSeat } from '../../core/types';
import { calculateRoundScore, getTeamOf, levelToRank } from '../../core/engine';
import { Trophy, ArrowRight, RotateCcw, RefreshCw, Film } from 'lucide-react';

interface RoundEndModalProps {
  gameState: GameState;
  onNextRound: () => void;
  onRestartMatch: () => void;
  onSwapRematch?: () => void;
  onReviewReplay?: () => void;
}

export const RoundEndModal: React.FC<RoundEndModalProps> = ({
  gameState,
  onNextRound,
  onRestartMatch,
  onSwapRematch,
  onReviewReplay,
}) => {
  const { finishedOrder, teamLevels, phase } = gameState;
  const isMatchEnd = phase === 'match_end';

  const mode = gameState.mode || '4p';
  const is6p = mode === '6p';
  const roundScore = calculateRoundScore(finishedOrder, mode);

  const seatNames: Record<number, string> = is6p
    ? {
        0: '我 (南)',
        1: '东南 (对方1)',
        2: '东北 (搭档1)',
        3: '正北 (对方2)',
        4: '西北 (搭档2)',
        5: '西南 (对方3)',
      }
    : {
        0: '我 (南)',
        1: '右家 (东)',
        2: '对家·搭档 (北)',
        3: '左家 (西)',
      };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md animate-fade-in overflow-y-auto">
      <div className="bg-slate-900 border-2 border-amber-500/50 rounded-2xl sm:rounded-3xl max-w-md landscape:max-w-2xl w-full p-3 sm:p-5 shadow-2xl text-center max-h-[95dvh] flex flex-col justify-between overflow-y-auto">
        {/* Main Content Grid: 1 column on Portrait, 2 columns on Landscape */}
        <div className="grid grid-cols-1 landscape:grid-cols-2 gap-3 items-center">
          {/* Left Column: Trophy, Title & Score Summary */}
          <div className="flex flex-col items-center justify-center space-y-2">
            {/* Trophy Icon */}
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500/20 border-2 border-amber-500/40 rounded-full flex items-center justify-center text-amber-400 shrink-0">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>

            {/* Title */}
            <div>
              <h2 className="text-lg sm:text-2xl font-black text-white">
                {isMatchEnd ? `🏆 恭喜【${roundScore.winningTeam === 0 ? '我方' : '对方'}】过A夺冠！` : '本局对战完成'}
              </h2>
              <p className="text-amber-400 font-bold text-xs sm:text-sm mt-0.5">
                {isMatchEnd ? '过A成功，包揽大局总胜利！' : roundScore.scoreDescription}
              </p>
            </div>

            {/* Level Progression (Red Team 0 vs Blue Team 1) */}
            <div className="grid grid-cols-2 gap-2 text-xs w-full">
              <div className="bg-rose-950/30 p-1.5 sm:p-2 rounded-xl border border-rose-500/40 shadow">
                <div className="text-[9px] sm:text-[10px] text-rose-400 font-black flex items-center justify-center gap-1">
                  <span>🔴</span>
                  <span>我方进度</span>
                </div>
                <div className="text-sm sm:text-base font-black text-rose-200">
                  打【{levelToRank(teamLevels[0])}】
                  {teamLevels[0] === 14 && (
                    <span className="text-[10px] text-amber-300 ml-1 block font-bold">
                      (第 {Math.min(3, (gameState.aAttempts?.[0] || 0) + 1)}/3 次冲A)
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-sky-950/30 p-1.5 sm:p-2 rounded-xl border border-sky-500/40 shadow">
                <div className="text-[9px] sm:text-[10px] text-sky-400 font-black flex items-center justify-center gap-1">
                  <span>🔵</span>
                  <span>对方进度</span>
                </div>
                <div className="text-sm sm:text-base font-black text-sky-200">
                  打【{levelToRank(teamLevels[1])}】
                  {teamLevels[1] === 14 && (
                    <span className="text-[10px] text-amber-300 ml-1 block font-bold">
                      (第 {Math.min(3, (gameState.aAttempts?.[1] || 0) + 1)}/3 次冲A)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Finishing Order Rank List */}
          <div className="bg-slate-950/70 border border-slate-800 rounded-xl p-2 divide-y divide-slate-800 text-xs flex flex-col justify-center">
            {finishedOrder.map((seat, index) => {
              const isMyTeam = getTeamOf(seat, mode) === 0;
              return (
                <div key={seat} className="py-1 flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <span
                      className={`w-4.5 h-4.5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0 ${
                        index === 0
                          ? 'bg-amber-500 text-slate-950'
                          : index === 1
                          ? 'bg-slate-300 text-slate-900'
                          : index === 2
                          ? 'bg-amber-700 text-white'
                          : 'bg-slate-700 text-slate-300'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="font-semibold text-slate-200 text-[11px]">{seatNames[seat]}</span>
                  </div>
                  <span
                    className={`text-[9px] px-2 py-0.2 rounded font-black flex items-center gap-0.5 shadow-sm ${
                      isMyTeam
                        ? 'bg-rose-950/80 text-rose-200 border border-rose-600/50'
                        : 'bg-sky-950/80 text-sky-200 border border-sky-600/50'
                    }`}
                  >
                    <span>{isMyTeam ? '🔴 我方' : '🔵 对方'}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons Grid (Bottom Bar) */}
        <div className="space-y-1.5 pt-2 border-t border-slate-800/80 mt-2">
          {/* Main Progression */}
          {isMatchEnd ? (
            <button
              onClick={onRestartMatch}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black py-2 rounded-xl shadow-lg flex items-center justify-center space-x-1.5 text-xs sm:text-sm transition-transform active:scale-95"
            >
              <RotateCcw className="w-4 h-4" />
              <span>开启新一轮大战 (从打2开始)</span>
            </button>
          ) : (
            <button
              onClick={onNextRound}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black py-2 rounded-xl shadow-lg flex items-center justify-center space-x-1.5 text-xs sm:text-sm transition-transform active:scale-95"
            >
              <span>进入下一局 (当前打【{gameState.levelRank}】)</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {/* Replay & Swap Rematch Sub-buttons */}
          <div className="grid grid-cols-2 gap-2">
            {onSwapRematch && (
              <button
                onClick={onSwapRematch}
                className="bg-slate-800 hover:bg-slate-700 text-amber-300 font-bold py-1.5 rounded-xl text-[11px] border border-amber-500/30 flex items-center justify-center gap-1 transition-transform active:scale-95"
                title="与对手互换手牌重打本局"
              >
                <RefreshCw className="w-3 h-3" />
                <span>🔄 换牌复赛</span>
              </button>
            )}

            {onReviewReplay && (
              <button
                onClick={onReviewReplay}
                className="bg-slate-800 hover:bg-slate-700 text-sky-300 font-bold py-1.5 rounded-xl text-[11px] border border-sky-500/30 flex items-center justify-center gap-1 transition-transform active:scale-95"
                title="进入复盘模式查看每一步出牌"
              >
                <Film className="w-3 h-3" />
                <span>🎬 立即复盘</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
